import { Component, FunctionComponent } from "react";
import { Storage as S3 } from "aws-amplify";
import Autocomplete from "react-google-autocomplete";
import { Form, Alert, Card, Col, Row } from "react-bootstrap";
import { RouteComponentProps } from "react-router-dom";
import { Button, variant } from "../form/Button";
import { AddressDetails, IReferShop } from "../../client/core";
import ImageUploader from "../form/ImageUploader";

export interface ReferShopState {
    email: string;
    name: string;
    addressId: string;
    phone: string;
    whatsapp: string;
    instagram: string;
    facebook: string;
    logo?: File;
    logoName: string;
    error: string;
    success: string;
    description: string;
}

export interface ReferShopProps extends RouteComponentProps {
    googleApiKey: string;
    imageStore: typeof S3;
    referShopAPI: IReferShop
}

export default class ReferShop extends Component<ReferShopProps, ReferShopState> {
    constructor(props: ReferShopProps) {
        super(props);
        this.state = {
            email: "",
            name: "",
            addressId: "",
            facebook: "",
            instagram: "",
            phone: "",
            whatsapp: "",
            logoName: "",
            description: "",
            error: "",
            success: "",
        };
    }

    handleSubmit = async () => {
        if (this.state.logo) {
            const imageName = `referral/logos/${this.state.email}/default`
            this.props.imageStore.put(
                imageName,
                this.state.logo,
                {
                    contentType: "image/jpeg",
                }
            );
        }

        this.props.referShopAPI.GetReferredShop({
            email: this.state.email,
            facebook: this.state.facebook,
            instagram: this.state.instagram,
            addressId: this.state.addressId,
            name: this.state.name,
            phone: this.state.phone,
            whatsapp: this.state.whatsapp,
            description: this.state.description,
        }).then(response => {
            if (response.statusCode != 204) {
                return this.setState({
                    error: `Failed to add shop details because of status ${response.statusCode}`
                })
            }
            this.setState({
                success: "You’ve joined a movement to bring local shopping back to life. We’ll be in touch by email in the next few hours to confirm your details and guide you through getting started."
            })
        })
    }

    handleAddressChange = (place: google.maps.places.PlaceResult) => {
        const addressDetails: AddressDetails = {
            id: place.place_id!,
            address: place.formatted_address!,
        };
        this.setState({
            addressId: addressDetails.id,
        });
    };

    render() {
        const props: ReferShopFormProps = {
            logoName: this.state.logoName,
            error: this.state.error,
            success: this.state.success,
            googleApiKey: this.props.googleApiKey,
            onAddressChange: this.handleAddressChange,
            onEmailChange: email => { this.setState({ email: email }) },
            onNameChange: name => { this.setState({ name: name }) },
            onPhoneChange: phone => { this.setState({ phone: phone }) },
            onFacebookChange: facebook => { this.setState({ facebook: facebook }) },
            onInstagramChange: instagram => { this.setState({ instagram: instagram }) },
            onWhatsappChange: whatsapp => { this.setState({ whatsapp: whatsapp }) },
            onLogoChange: (name, logo) => { this.setState({ logo: logo, logoName: name }) },
            onDescriptionChange: (description) => { this.setState({ description: description }) },
            onSubmit: this.handleSubmit
        };

        if (this.state.success !== "") {
            return <ReferShopConfirmation {...props} />
        }

        return <ReferShopRegistrationForm {...props} />;
    }
}

export interface ReferShopFormProps {
    logoName: string;
    error: string;
    success: string;
    googleApiKey: string;
    onEmailChange: (email: string) => void;
    onNameChange: (name: string) => void;
    onPhoneChange: (phone: string) => void;
    onFacebookChange: (facebook: string) => void;
    onInstagramChange: (instagram: string) => void;
    onWhatsappChange: (whatsapp: string) => void;
    onAddressChange: (address: google.maps.places.PlaceResult) => void;
    onLogoChange: (name: string, logo: File) => void;
    onDescriptionChange: (description: string) => void;
    onSubmit: () => void;
}

export const ReferShopConfirmation: FunctionComponent<ReferShopFormProps> = (props) => (
    <Form>
        <h2>🎉 You’re in!</h2>
        <p>{props.success}</p>
    </Form >
);

export const ReferShopRegistrationForm: FunctionComponent<ReferShopFormProps> = (props) => (
    <Form>
        <h2>Enter Details</h2>
        <Alert variant={"danger"} show={props.error !== ""}>
            {props.error}
        </Alert>
        <Alert variant={"success"} show={props.success !== ""}>
            {props.success}
        </Alert>
        <Row style={{ marginTop: "1rem" }}>
            <Row>
                <ImageUploader onUpload={file => props.onLogoChange(file.name, file)} />
            </Row>
            <Row>
                <Col xs={12}>
                    <h5 style={{ textAlign: "center", marginTop: "1rem" }}>Contact</h5>
                    <Col style={{ marginBottom: "1rem" }} xs={12}>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="email"
                            name={"email"}
                            id={"email"}
                            onChange={(e) => props.onEmailChange(e.target.value)}
                            placeholder="Enter shop email"
                        />
                    </Col>
                    <Col style={{ marginBottom: "1rem" }} xs={12}>
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            type="text"
                            name={"name"}
                            id={"name"}
                            onChange={(e) => props.onNameChange(e.target.value)}
                            placeholder="Enter shop name"
                        />
                    </Col>
                    <Col style={{ marginBottom: "1rem" }} xs={12}>
                        <Form.Label>Phone (Optional)</Form.Label>
                        <Form.Control
                            type="text"
                            name={"phone"}
                            id={"phone"}
                            onChange={(e) => props.onPhoneChange(e.target.value)}
                            placeholder="Enter shop name"
                        />
                    </Col>
                </Col>
                <Col xs={12}>
                    <h5 style={{ textAlign: "center", marginTop: "1rem" }}>Social Media</h5>
                    <Col style={{ marginBottom: "1rem" }} xs={12}>
                        <Form.Label>Whatsapp (Optional)</Form.Label>
                        <Form.Control
                            type="text"
                            name={"whatsapp"}
                            id={"whatsapp"}
                            onChange={(e) => props.onWhatsappChange(e.target.value)}
                            placeholder="Enter Whatsapp number"
                        />
                    </Col>
                    <Col style={{ marginBottom: "1rem" }} xs={12}>
                        <Form.Label>Instagram (Optional)</Form.Label>
                        <Form.Control
                            type="text"
                            name={"instagram"}
                            id={"instagram"}
                            onChange={(e) => props.onInstagramChange(e.target.value)}
                            placeholder="Enter Instagram tag"
                        />
                    </Col>
                    <Col style={{ marginBottom: "1rem" }} xs={12}>
                        <Form.Label>Facebook (Optional)</Form.Label>
                        <Form.Control
                            type="text"
                            name={"facebook"}
                            id={"facebook"}
                            onChange={(e) => props.onFacebookChange(e.target.value)}
                            placeholder="Enter Facebook page ID"
                        />
                    </Col>
                </Col>
            </Row>
            <Row>
                <Col style={{ marginBottom: "1rem" }} sm={12}>
                    <h5 style={{ textAlign: "center", marginTop: "1rem" }}>Navigation</h5>
                    <Form.Label>Shop Address</Form.Label>
                    <Autocomplete
                        apiKey={props.googleApiKey}
                        onPlaceSelected={(place) => props.onAddressChange(place)}
                        style={{
                            width: "100%",
                            padding: ".375rem .75rem",
                            borderRadius: ".25rem",
                            border: "1px solid #ced4da",
                        }}
                        options={{
                            types: ["address"],
                        }}
                    />
                </Col>
            </Row>
            <Row>
                <Col style={{ marginBottom: "1rem" }} sm={12}>
                    <h5 style={{ textAlign: "center", marginTop: "1rem" }}>Details</h5>
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as={"textarea"}
                        rows={4}
                        name={"description"}
                        id={"description"}
                        onChange={(e) => props.onDescriptionChange(e.target.value)}
                        placeholder="Enter a short description of what your shops all about"
                    />
                </Col>
            </Row>
            <Row>
                <Col style={{ marginBottom: "1rem", marginTop: "1rem", textAlign: "right" }} xs={12}>
                    <Button
                        style={{ width: "100%", padding: ".5rem" }}
                        buttonvariant={variant.Primary}
                        name={"Submit"}
                        onClick={() => props.onSubmit()}
                    />
                </Col>
            </Row>
        </Row>
    </Form >
);
